const PROMPT_DRIVEN_TASK_TYPES = new Set([1]);
const LEGACY_OVERLAY_ASSET_NAME = "sticker";

/**
 * Photo (1) AI vs standard is stored as a prompt, not task_subtype.
 * The API keeps task_subtype at "0" for Photo. Text (0) uses stored
 * task_subtype ("0" or "1"), set by the type card or the legacy dropdown.
 * @param {number|string} taskType
 * @returns {boolean}
 */
export const isPromptDrivenTaskType = (taskType) => (
    PROMPT_DRIVEN_TASK_TYPES.has(Number(taskType))
);

/**
 * Subtype to persist when a type card is selected.
 * Photo keeps "0" so AI vs standard can be inferred from the prompt later.
 * Text persists "0" or "1" immediately from the selected card.
 * @param {{ taskType: number, subtype?: number }} option
 * @returns {string|undefined}
 */
export const persistedSubtypeForTypeSelection = (option) => {
    if (isPromptDrivenTaskType(option.taskType)) {
        return "0";
    }

    if (option.subtype === undefined) {
        return undefined;
    }

    return String(option.subtype);
};

/**
 * Legacy background/sticker uploads store the overlay as the `sticker` photo
 * asset. Preserve any existing overlay; otherwise seed the provided default.
 * @param {{ photos?: Array<{ name?: string, url?: string, urls?: { png?: string } }> }} [assets]
 * @returns {boolean}
 */
export const hasLegacyOverlay = (assets) => (
    (assets?.photos || []).some((asset) => (
        asset?.name === LEGACY_OVERLAY_ASSET_NAME && Boolean(asset?.urls?.png || asset?.url)
    ))
);

const BUNDLED_OVERLAY_URL_PATTERN = /sample-background|\/static\/media\//;

const getLegacyOverlayUrl = (assets) => {
    const overlay = (assets?.photos || []).find((asset) => asset?.name === LEGACY_OVERLAY_ASSET_NAME);
    return overlay?.urls?.png || overlay?.url || "";
};

/**
 * Photo/Video Inpainting should upload the bundled default background only when
 * the challenge has no remote overlay yet. A webpack `/static/media/` URL is
 * local-only and still needs a signed upload.
 * @param {Object} [assets]
 * @param {string|null} [overlayMediaUuid]
 * @returns {boolean}
 */
export const shouldSeedDefaultLegacyOverlay = (assets, overlayMediaUuid) => {
    if (overlayMediaUuid) {
        return false;
    }

    const overlayUrl = getLegacyOverlayUrl(assets);
    if (!overlayUrl) {
        return true;
    }

    return BUNDLED_OVERLAY_URL_PATTERN.test(String(overlayUrl));
};

/**
 * @param {{
 *   assets?: Object,
 *   overlayUrl?: string,
 *   overlayUuid?: string|null,
 *   overlayExtension?: string,
 * }} params
 * @returns {Object}
 */
export const getDefaultLegacyOverlayPayload = ({
    assets,
    overlayUrl,
    overlayUuid = null,
    overlayExtension = "png",
}) => {
    if (!overlayUrl) {
        return {};
    }

    return {
        assets: {
            ...(assets || {}),
            photos: [
                ...(assets?.photos || []).filter((asset) => asset?.name !== LEGACY_OVERLAY_ASSET_NAME),
                { name: LEGACY_OVERLAY_ASSET_NAME, url: overlayUrl },
            ],
        },
        overlay_media_uuid: overlayUuid,
        overlay_media_extension: overlayExtension,
    };
};

/**
 * The API resets `task_subtype` when `task_type` is in the same write.
 * Legacy saved them separately (type radio, then subtype dropdown).
 * @param {{
 *   option: { taskType: number, reset?: Object, defaultVideoLength?: number },
 *   currentTaskType: number|string,
 *   selectedSubtype?: string,
 *   shouldApplyDefaultVideoLength?: boolean,
 * }} params
 * @returns {Object[]} sequential challenge payloads
 */
export const getTypeSelectionWrites = ({
    option,
    currentTaskType,
    selectedSubtype,
    shouldApplyDefaultVideoLength = false,
}) => {
    const taskTypeChanged = String(currentTaskType) !== String(option.taskType);
    const typePayload = {
        ...(taskTypeChanged ? { task_type: option.taskType } : {}),
        ...(shouldApplyDefaultVideoLength ? { video_length: option.defaultVideoLength } : {}),
        ...(option.reset || {}),
    };
    const subtypePayload = selectedSubtype === undefined
        ? {}
        : { task_subtype: selectedSubtype };

    if (taskTypeChanged && Object.keys(subtypePayload).length) {
        return [typePayload, subtypePayload].filter((payload) => Object.keys(payload).length);
    }

    const combined = { ...typePayload, ...subtypePayload };
    return Object.keys(combined).length ? [combined] : [];
};

/**
 * @param {string} [prompt]
 * @returns {boolean}
 */
export const hasTransformationPrompt = (prompt) => Boolean(String(prompt ?? '').trim());

/**
 * Derive the UI subtype for prompt-driven challenge types (Photo).
 * @param {string} [prompt]
 * @returns {"0"|"1"}
 */
export const subtypeFromPrompt = (prompt) => (hasTransformationPrompt(prompt) ? "1" : "0");
